import axios from "axios";
import Utils from "./Utils";

const IDLE_TIMEOUT_MS = 30000;
const TICK_INTERVAL_MS = 1000;
const USERDATA_SYNC_INTERVAL_MS = 5000;
const SYNC_INTERVAL_MS = 15000;
const QUEUE_STORAGE_KEY = `${process.env.REACT_APP_APPNAME}-time-tracker-queue`;

let trackerInstance = null;
const trackerListeners = new Set();

const normalizeQueueEntries = (entries) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  const merged = new Map();

  entries.forEach((entry) => {
    const pagePath = String(entry?.page_path || "");
    if (!pagePath) {
      return;
    }

    const key = pagePath;
    const existing = merged.get(key);
    const durationSec = Math.max(0, Number(entry?.duration_sec) || 0);

    if (existing) {
      existing.duration_sec += durationSec;
      existing.captured_at = new Date().toISOString();
      return;
    }

    merged.set(key, {
      duration_sec: durationSec,
      page_path: pagePath,
      captured_at: entry?.captured_at || new Date().toISOString(),
    });
  });

  return Array.from(merged.values());
};

const getSnapshot = () => {
  const state = trackerInstance;
  return {
    elapsedMs: state ? Math.max(0, Math.round(state.totalMs)) : 0,
    isRunning: state
      ? state.isFocused &&
        !state.isManualPaused &&
        (!state.isIdle || state.remotePending > 0) &&
        isTrackableUser()
      : false,
    isManualPaused: state ? state.isManualPaused : false,
    isTrackable: isTrackableUser(),
  };
};

const publishState = () => {
  const snapshot = getSnapshot();
  trackerListeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (_) {}
  });
};

export const subscribeActivityTracker = (listener) => {
  if (typeof listener !== "function") {
    return () => {};
  }
  trackerListeners.add(listener);
  listener(getSnapshot());
  return () => {
    trackerListeners.delete(listener);
  };
};

export const toggleManualPause = () => {
  if (!trackerInstance) return;
  trackerInstance.isManualPaused = !trackerInstance.isManualPaused;
  publishState();
};

const safeParseQueue = () => {
  try {
    const raw = localStorage.getItem(QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return normalizeQueueEntries(parsed);
  } catch (e) {
    return [];
  }
};

const saveQueue = (queue) => {
  localStorage.setItem(
    QUEUE_STORAGE_KEY,
    JSON.stringify(normalizeQueueEntries(queue)),
  );
};

const isTrackableUser = () => {
  const user = Utils.getUserData();
  return Boolean(user && user.token);
};

const persistElapsedToUserData = (elapsedMs) => {
  const user = Utils.getUserData();
  if (!user || !user.token) {
    return;
  }

  Utils.addToUserData({
    activityTimerMs: Math.max(0, Math.round(elapsedMs)),
    activityTimerUpdatedAt: new Date().toISOString(),
  });
};

const sumDurationSec = (entries) =>
  entries.reduce(
    (total, entry) => total + Math.max(0, Number(entry?.duration_sec) || 0),
    0,
  );

const markDurationSyncedToDb = (durationSec) => {
  if (!durationSec || durationSec <= 0) {
    return;
  }

  const user = Utils.getUserData();
  if (!user || !user.token) {
    return;
  }

  const currentTotalMs = Math.max(0, Number(user.activityTimerMs) || 0);
  const currentSyncedMs = Math.max(
    0,
    Number(user.activityTimerDbSyncedMs) || 0,
  );

  Utils.addToUserData({
    activityTimerDbSyncedMs: Math.min(
      currentTotalMs,
      currentSyncedMs + Math.round(durationSec * 1000),
    ),
    activityTimerDbSyncedAt: new Date().toISOString(),
  });
};

const enqueueUnsyncedLocalTimerChunk = () => {
  const user = Utils.getUserData();
  if (!user || !user.token) {
    return;
  }

  const currentTotalMs = Math.max(0, Number(user.activityTimerMs) || 0);
  const currentSyncedMs = Math.max(
    0,
    Number(user.activityTimerDbSyncedMs) || 0,
  );
  const queuedMs = sumDurationSec(safeParseQueue()) * 1000;
  const unsyncedMs = Math.max(0, currentTotalMs - currentSyncedMs - queuedMs);

  if (unsyncedMs > 0) {
    enqueueChunk(unsyncedMs);
  }
};

const enqueueChunk = (durationMs) => {
  if (!durationMs || durationMs <= 0 || !isTrackableUser()) {
    return;
  }

  const queue = safeParseQueue();
  const pagePath = `${window.location.pathname}${window.location.search}`;
  const match = queue.find((entry) => entry.page_path === pagePath);
  const durationSec = Math.max(0, Math.round(durationMs / 1000));

  if (durationSec <= 0) {
    return;
  }

  if (match) {
    match.duration_sec += durationSec;
    match.captured_at = new Date().toISOString();
  } else {
    queue.push({
      duration_sec: durationSec,
      page_path: pagePath,
      captured_at: new Date().toISOString(),
    });
  }

  saveQueue(queue);
};

const syncQueue = async () => {
  if (!isTrackableUser()) {
    return;
  }

  const queue = safeParseQueue();
  if (queue.length === 0) {
    return;
  }

  const batch = queue.slice(0, 50);

  try {
    const response = await axios.post(
      Utils.apiUrl("activity/time"),
      {
        app: "frontend",
        entries: batch,
      },
      Utils.apiHeaders(),
    );

    if (response?.data?.success) {
      markDurationSyncedToDb(sumDurationSec(batch));
      const batchKeys = new Set(batch.map((entry) => entry.page_path));
      const remaining = safeParseQueue().filter(
        (entry) => !batchKeys.has(entry.page_path),
      );
      saveQueue(remaining);
    }
  } catch (e) {
    // Keep queue for retry on next sync.
  }
};

const syncQueueKeepAlive = () => {
  if (!isTrackableUser()) {
    return;
  }

  const queue = safeParseQueue();
  if (queue.length === 0) {
    return;
  }

  const batch = queue.slice(0, 50);

  axios
    .post(
      Utils.apiUrl("activity/time"),
      {
        app: "frontend",
        unload: true,
        entries: batch,
      },
      Utils.apiHeaders(),
    )
    .then((response) => {
      if (!response?.data?.success) {
        throw new Error("Failed to sync page time on unload");
      }
      markDurationSyncedToDb(sumDurationSec(batch));
      const batchKeys = new Set(batch.map((entry) => entry.page_path));
      const remaining = safeParseQueue().filter(
        (entry) => !batchKeys.has(entry.page_path),
      );
      saveQueue(remaining);
    })
    .catch(() => {
      // Keep queue for later retries.
    });
};

export const notifyRemoteCallStarted = () => {
  if (!trackerInstance) return;
  trackerInstance.remotePending += 1;
  publishState();
};

export const notifyRemoteCallFinished = () => {
  if (!trackerInstance) return;
  trackerInstance.remotePending = Math.max(
    0,
    trackerInstance.remotePending - 1,
  );
  publishState();
};

export const initActivityTracker = () => {
  if (trackerInstance) {
    return trackerInstance.stop;
  }

  const state = {
    totalMs: 0,
    runningMs: 0,
    lastTickAt: Date.now(),
    lastUserDataSyncAt: Date.now(),
    isFocused: document.visibilityState !== "hidden",
    isIdle: false,
    isManualPaused: false,
    remotePending: 0,
    idleTimerId: null,
    tickIntervalId: null,
    syncIntervalId: null,
  };

  trackerInstance = state;

  const shouldTick = () => {
    if (!isTrackableUser()) {
      return false;
    }
    if (state.isManualPaused) {
      return false;
    }
    return state.isFocused && (!state.isIdle || state.remotePending > 0);
  };

  const resetIdleTimer = () => {
    if (state.idleTimerId) {
      clearTimeout(state.idleTimerId);
    }
    state.isIdle = false;
    state.idleTimerId = window.setTimeout(() => {
      state.isIdle = true;
      publishState();
    }, IDLE_TIMEOUT_MS);
    publishState();
  };

  const flushRunningMs = () => {
    if (state.runningMs <= 0) {
      return;
    }
    enqueueChunk(state.runningMs);
    state.runningMs = 0;
  };

  const onActivity = () => {
    if (!state.isFocused) return;
    resetIdleTimer();
  };

  const onFocus = () => {
    state.isFocused = true;
    resetIdleTimer();
    publishState();
  };

  const onBlur = () => {
    state.isFocused = false;
    flushRunningMs();
    persistElapsedToUserData(state.totalMs);
    publishState();
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      onBlur();
      return;
    }
    onFocus();
  };

  const onPageExit = () => {
    flushRunningMs();
    persistElapsedToUserData(state.totalMs);
    enqueueUnsyncedLocalTimerChunk();
    syncQueueKeepAlive();
  };

  const tick = () => {
    const now = Date.now();
    const delta = now - state.lastTickAt;
    state.lastTickAt = now;

    if (shouldTick()) {
      state.runningMs += delta;
      state.totalMs += delta;

      if (now - state.lastUserDataSyncAt >= USERDATA_SYNC_INTERVAL_MS) {
        persistElapsedToUserData(state.totalMs);
        state.lastUserDataSyncAt = now;
      }
    }
    publishState();
  };

  state.tickIntervalId = window.setInterval(tick, TICK_INTERVAL_MS);
  state.syncIntervalId = window.setInterval(() => {
    flushRunningMs();
    syncQueue();
  }, SYNC_INTERVAL_MS);

  ["mousemove", "scroll", "keydown", "click", "touchstart"].forEach((evt) => {
    window.addEventListener(evt, onActivity, { passive: true });
  });
  window.addEventListener("focus", onFocus);
  window.addEventListener("blur", onBlur);
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("beforeunload", onPageExit);
  window.addEventListener("unload", onPageExit);
  window.addEventListener("pagehide", onPageExit);

  resetIdleTimer();
  publishState();

  state.stop = () => {
    flushRunningMs();
    persistElapsedToUserData(state.totalMs);
    syncQueue();

    if (state.idleTimerId) clearTimeout(state.idleTimerId);
    if (state.tickIntervalId) clearInterval(state.tickIntervalId);
    if (state.syncIntervalId) clearInterval(state.syncIntervalId);

    ["mousemove", "scroll", "keydown", "click", "touchstart"].forEach((evt) => {
      window.removeEventListener(evt, onActivity);
    });
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("blur", onBlur);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("beforeunload", onPageExit);
    window.removeEventListener("unload", onPageExit);
    window.removeEventListener("pagehide", onPageExit);

    trackerInstance = null;
    publishState();
  };

  return state.stop;
};
